/*
 * File Created: Wednesday, 17th May 2023 8:47:20 am
 * Author: Chenzier Kabrabam
 * Copyright (c) - 2018-2023 Addble Solutions Private Limited. All Rights Reserved.
 */

import Box from "@mui/material/Box";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import React from "react";
import { useParams } from "react-router-dom";
import { staticData } from "../data/staticData";
import { Button, Divider, Rating, Tab, Tabs, Theme, Typography, useTheme } from "@mui/material";
import type { Item, SiteLink } from "../types/static/StaticModal.type";
import ImagePreview from "../components/ImagePreview/ImagePrevew";
import ViewProductDescription from "../components/ViewProductDescription/ViewProductDescription";
import { Colors } from "../constants/color";
import { FaRegThumbsDown, FaRegThumbsUp } from "react-icons/fa";
import ImagePreviewSkeleton from "../components/Skeleton/ImagePreview.skeleton";
import ViewProductDescriptionSkeleton from "../components/Skeleton/ViewProductDescription.skeleton";
import Suggestion from "../components/suggestion/Suggestion";

type TabPanelProps = {
  children?: React.ReactNode;
  index: number;
  value: number;
};

function TabPanel({ index, value, children }: TabPanelProps): JSX.Element {
  const theme: Theme = useTheme();
  return (
    <Box
      component="div"
      role="tabPanel"
      hidden={value !== index}
      id={`tabPanel-${index}`}
      aria-labelledby={`tab-${index}`}
    >
      {value === index && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            border: `1px solid ${Colors.silver}`,
            borderRadius: theme.spacing(),
            padding: theme.spacing(3),
          }}
        >
          {children}
        </Box>
      )}
    </Box>
  );
}

function allyProps(index: number) {
  return {
    id: `tab-${index}`,
    "aria-controls": `tabPanel-${index}`,
  };
}

function TabBody(): JSX.Element {
  return (
    <React.Fragment>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Typography variant="h6" sx={{ color: Colors.mine_shaft }}>Information</Typography>
        <Typography variant="body1" sx={{ color: Colors.boulder }}>
          Lorem ipsum dolor sit amet, consectetuer adipiscing elit. Donec odio. Quisque volutpat
          mattis eros. Nullam malesuada erat ut turpis. Suspendisse urna viverra non, semper
          suscipit, posuere a, pede. Donec nec justo eget felis facilisis fermentum. Aliquam
          porttitor mauris sit amet orci. Aenean dignissim pellentesque felis. Phasellus ultrices
          nulla quis nibh. Quisque a lectus. Donec consectetuer ligula vulputate sem tristique
          cursus.
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        {/** List */}
        <Typography variant="h6"sx={{ color: Colors.mine_shaft }}>Fabric & care</Typography>
        <Box>
          {[
            "Nunc nec porttitor turpis. In eu risus enim. In vitae mollis elit.",
            "Vivamus finibus vel mauris ut vehicula",
            "Vivamus finibus vel mauris ut vehicula",
          ].map((data, index) => {
            return (
              <Typography
                key={index}
                variant="body1"
                component="li"
                sx={{
                  color: Colors.boulder,
                  "&::marker": {
                    textAlign: "center",
                    fontSize: "12px",
                  },
                }}
              >
                {data}
              </Typography>
            );
          })}
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Typography variant="h6" sx={{ color: Colors.mine_shaft }}>Size</Typography>
        <Typography variant="body1" sx={{ color: Colors.boulder }}>One Size</Typography>
      </Box>
    </React.Fragment>
  );
}

function ProductReview(): JSX.Element {
  dayjs.extend(relativeTime);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 3,
        }}
      >
        <Box
          sx={{
            width: "300px",
            display: "flex",
            flexDirection: "column",
            gap: 2
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: Colors.mine_shaft,
              fontWeight: 400,
            }}
          >
            Chenzier Kabrabam
          </Typography>
          <Box>
          <Rating size="small" value={4} readOnly />
          <Typography variant="body1" sx={{ color: Colors.silver }}>
            {dayjs(1685593500000).fromNow()}
          </Typography>
          </Box>
          {/* <Typography>{dayjs(1685852654000).format("DD/MM/YYYY hh:mm:ss a")}</Typography> */}
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: Colors.mine_shaft,
              fontWeight: 400,
            }}
          >
            Good, perfect size
          </Typography>
          <Box>
            <Typography
              variant="body1"
              sx={{
                color: Colors.boulder,
                fontWeight: "400",
                fontSize: "14px",
              }}
            >
              Lorem ipsum dolor sit amet, consectetur adipisicing elit. Ducimus cum dolores
              assumenda asperiores facilis porro reprehenderit animi culpa atque blanditiis commodi
              perspiciatis doloremque, possimus, explicabo, autem fugit beatae quae voluptas!
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 2,
              alignItems: "center",
            }}
          >
            <Button
              size="small"
              startIcon={<FaRegThumbsUp style={{ fontSize: "12px"}} />}
              sx={{
                height: "30px",
                borderRadius: 0,
                textTransform: "capitalize",
                color: Colors.boulder,
                fontWeight: "400",
                fontSize: "12px",
                // color: Colors.silver,
                "&:hover": {
                  backgroundColor: "transparent",
                  borderBottom: `1px solid ${Colors.fallow}`,
                  color: Colors.fallow,
                }
              }}
            >
              Helpful (2)
            </Button>
            <Button
              size="small"
              startIcon={<FaRegThumbsDown style={{ fontSize: "12px"}} />}
              sx={{
                height: "30px",
                borderRadius: 0,
                color: Colors.boulder,
                textTransform: "capitalize",
                fontWeight: "400",
                fontSize: "12px",
                "&:hover": {
                  backgroundColor: "transparent",
                  borderBottom: `1px solid ${Colors.fallow}`,
                  color: Colors.fallow,
                }
              }}
            >
              Unhelpful (0)
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function ProductAdditionalInformation(): JSX.Element {
  const [value, setValue] = React.useState(0);

  function handleTabOnChange(event: React.SyntheticEvent, newValue: number) {
    setValue(newValue);
  }
  return (
    <Box
      sx={{
        width: "100%",
        marginTop: (theme) => theme.spacing(7),
        // backgroundColor: "green",
      }}
    >
      <Box>
        <Tabs
          aria-label="Tab"
          variant="standard"
          textColor="inherit"
          sx={{
            "& .MuiButtonBase-root": {
              textTransform: "capitalize",
              fontSize: "18px",
            },
            "& .Mui-selected": {
              color: Colors.fallow,
            },
            "& .MuiTabs-indicator": {
              backgroundColor: Colors.fallow,
            },
          }}
          value={value}
          onChange={handleTabOnChange}
          centered
        >
          <Tab label="Additional Information" {...allyProps(0)} />
          <Tab label={`Review (${2})`} {...allyProps(0)} />
        </Tabs>
      </Box>
      <TabPanel value={value} index={0}>
        <TabBody />
      </TabPanel>
      <TabPanel value={value} index={1}>
        <Typography variant="h6">Review (2)</Typography>
        {[1, 2].map((a) => {
          return (
            <React.Fragment key={a}>
              <ProductReview />
              <Divider
                variant="middle"
                sx={{
                  "&:last-child": {
                    display: "none",
                  },
                }}
                flexItem
              />
            </React.Fragment>
          );
        })}
      </TabPanel>
    </Box>
  );
}

function ViewProduct(): JSX.Element {
  const theme: Theme = useTheme();
  const { productID } = useParams();
  const [productDetail, setProductDetail] = React.useState<
    Item & { socialMedia: Array<SiteLink> }
  >();
  console.log("VIEW PRODUCT : PRODUCT ID : ", productID);
  console.log("VIEW PRODUCT : PRODUCT DETAIL : ", productDetail);

  React.useEffect(() => {
    window.scrollTo(0,0);
  }, [])

  const getProductDetail = (itemID: string) => {
    let foundItem: Item & { socialMedia: Array<SiteLink> } = {} as Item & {
      socialMedia: Array<SiteLink>;
    };
    staticData.sections.forEach((section) => {
      const found = section.items.find((item) => item.id === itemID);
      if (found !== undefined) {
        const modifiedData = { ...found, socialMedia: staticData.siteProfile.socialMedia };
        Object.assign(foundItem, modifiedData);
      }
      return null;
    });
    return foundItem;
  };

  React.useEffect(() => {
    if (productID !== undefined) {
      setProductDetail(getProductDetail(productID));
    }
  }, [productID]);

  return (
    <React.Fragment>
      {/* <Box sx={{ ...theme.mixins.toolbar }} /> */}
      <Box
        sx={{
          width: { xs: "100%", xl: "90%" },
          display: "flex",
          // alignItems: "center",
          justifyContent: "center",
          // backgroundColor: "grey",
          flexDirection: { xs: "column" },
          gap: { xs: 2, md: 1 },
          padding: {
            xs: theme.spacing(3, 1),
            md: theme.spacing(3, 2),
            lg: theme.spacing(3, 8),
            xl: theme.spacing(3, 30),
          },
        }}
      >
        {productDetail === undefined || Object.keys(productDetail).length === 0 ?
          (
            <React.Fragment>
              <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 3.5, md: 2 },
              }}
            >
              <ImagePreviewSkeleton />
              <ViewProductDescriptionSkeleton />
            </Box>
            </React.Fragment>
          ) : (
          <React.Fragment>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 3.5, md: 2 },
              }}
            >
              <ImagePreview images={productDetail.images} />
              <ViewProductDescription
                category={productDetail.category}
                description={productDetail.description}
                name={productDetail.name}
                discount={productDetail.discount}
                mrp={productDetail.mrp}
                id={productDetail.id}
                share={productDetail.socialMedia}
              />
            </Box>
            <ProductAdditionalInformation />
            <Suggestion />
          </React.Fragment>
        )}
      </Box>
    </React.Fragment>
  );
}

export default ViewProduct;
